import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertTriangle } from "lucide-react";

export default function HlsPlayer({ videoId, poster }) {
  const videoRef = useRef(null);
  const { user } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const viewCounted = useRef(false);
  const lastProgress = useRef(0);
  const completed = useRef(false);

  useEffect(() => {
    let hls;
    let cancelled = false;
    viewCounted.current = false;
    completed.current = false;

    async function setup() {
      setStatus("loading");
      try {
        const { data } = await api.get(`/videos/${videoId}/playback`);
        if (cancelled) return;
        const url = data.playback_url;
        const el = videoRef.current;
        if (!el) return;

        if (Hls.isSupported()) {
          hls = new Hls({ maxBufferLength: 30 });
          hls.loadSource(url);
          hls.attachMedia(el);
          hls.on(Hls.Events.MANIFEST_PARSED, () => setStatus("ready"));
          hls.on(Hls.Events.ERROR, (e, d) => {
            if (d.fatal) setStatus("error");
          });
        } else if (el.canPlayType("application/vnd.apple.mpegurl")) {
          el.src = url;
          setStatus("ready");
        } else {
          setStatus("error");
        }

        // resume position for logged users
        if (user) {
          try {
            const r = await api.get(`/account/resume/${videoId}`);
            if (r.data.position > 5) el.currentTime = r.data.position;
          } catch (_) {}
        }
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    }
    setup();
    return () => {
      cancelled = true;
      if (hls) hls.destroy();
    };
  }, [videoId, user]);

  const onPlay = () => {
    if (!viewCounted.current) {
      viewCounted.current = true;
      let source = "direct";
      try {
        const params = new URLSearchParams(window.location.search);
        source = params.get("utm_source") || params.get("ref") ||
          (document.referrer ? new URL(document.referrer).hostname : "direct");
      } catch (_) {}
      api.post("/events", { video_id: videoId, type: "view", source }).catch(() => {});
    }
  };

  const onTimeUpdate = (e) => {
    const el = e.target;
    if (el.currentTime - lastProgress.current >= 15) {
      const delta = el.currentTime - lastProgress.current;
      lastProgress.current = el.currentTime;
      api.post("/events", { video_id: videoId, type: "progress", seconds: delta }).catch(() => {});
      if (user)
        api
          .post("/account/history", {
            video_id: videoId,
            position: el.currentTime,
            duration: el.duration,
          })
          .catch(() => {});
    }
  };

  const onEnded = (e) => {
    if (!completed.current) {
      completed.current = true;
      api.post("/events", { video_id: videoId, type: "complete" }).catch(() => {});
      if (user)
        api
          .post("/account/history", {
            video_id: videoId,
            position: e.target.duration,
            duration: e.target.duration,
            completed: true,
          })
          .catch(() => {});
    }
  };

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black ring-1 ring-white/10">
      <video
        ref={videoRef}
        data-testid="hls-player"
        poster={poster}
        controls
        playsInline
        onPlay={onPlay}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        className="h-full w-full bg-black"
      />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 text-center text-muted-foreground">
          <AlertTriangle className="h-8 w-8 text-primary" />
          <p className="max-w-xs text-sm">Lecture indisponible pour le moment.</p>
        </div>
      )}
    </div>
  );
}
